/**
 * Analyzer: Day ATM Strategy Spotlight
 *
 * When multiple ATM strategies were used, highlight the best/worst split.
 * When only one was used, comment on its performance if notable.
 */

import { CATEGORY, SEVERITY, makeObservation } from '../../types';

export const key = 'dayAtmSpotlight';
export const tier = 'free';

export const analyze = (ctx) => {
  const { day } = ctx;
  if (!day || !day.atm || !Array.isArray(day.atm.strategies)) return null;

  const list = day.atm.strategies;
  if (list.length === 0) return null;

  const best = list[0];
  const worst = list[list.length - 1];

  // Multi-ATM split: best positive, worst negative
  if (list.length >= 2 && best.pnl > 0 && worst.pnl < 0) {
    return makeObservation({
      id: `${key}:${day.date}:split`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.INFO,
      category: CATEGORY.EXECUTION,
      title: `ATM mix split the day`,
      detail: `"${best.name}" +$${best.pnl.toFixed(0)} (${best.winRate.toFixed(0)}% WR, ${best.avgR.toFixed(2)}R). "${worst.name}" −$${Math.abs(worst.pnl).toFixed(0)}.`,
      action: `Review stage params on "${worst.name}" — might be too aggressive for current volatility.`,
      evidence: { best, worst, all: list },
      metric: { label: 'Top ATM', value: best.name, unit: '' },
      confidence: 0.75,
      priority: 60
    });
  }

  // All ATM profitable (≥2 strategies)
  if (list.length >= 2 && worst.pnl > 0) {
    return makeObservation({
      id: `${key}:${day.date}:allwin`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: SEVERITY.SUCCESS,
      category: CATEGORY.EXECUTION,
      title: 'Every ATM strategy printed money',
      detail: `${list.length} strategies used, all green. Top: "${best.name}" +$${best.pnl.toFixed(0)}.`,
      action: 'Trail/stop params are dialed in for today\'s volatility. Check if the ATM version history needs a new checkpoint.',
      evidence: { all: list },
      confidence: 0.7,
      priority: 45
    });
  }

  // Single ATM — note its performance only if notable
  if (list.length === 1 && Math.abs(best.pnl) >= 50) {
    const positive = best.pnl > 0;
    return makeObservation({
      id: `${key}:${day.date}:solo`,
      analyzer: key,
      scope: 'day',
      scopeId: day.date,
      severity: positive ? SEVERITY.SUCCESS : SEVERITY.INFO,
      category: CATEGORY.EXECUTION,
      title: `Single ATM day: "${best.name}"`,
      detail: `${best.trades} trade${best.trades > 1 ? 's' : ''}: ${positive ? '+' : '−'}$${Math.abs(best.pnl).toFixed(0)} (${best.winRate.toFixed(0)}% WR, ${best.avgR.toFixed(2)}R).`,
      action: positive
        ? `Staying in one ATM kept you disciplined. Note the market read that made "${best.name}" the right call.`
        : `One-ATM days expose param fit. Check the stage settings against today's ranges before repeating.`,
      evidence: { strategy: best },
      confidence: 0.65,
      priority: 40
    });
  }

  return null;
};
